import i18n from "@/i18n";
import { debug } from "@/mixins/devUtil";
import { sentryClient } from "@/sentry";
import { useToastStore } from "@/stores/toast";

interface ErrorResult {
  summary: string;
  detail?: string;
  params?: Record<string, unknown>;
}

interface FormatErrorOptions {
  resource?: string;
}

const AUTH_ERROR_MAP: Record<string, ErrorResult> = {
  "auth/user-not-found": {
    summary: "error.auth.invalid_credentials.summary",
    detail: "error.auth.invalid_credentials.detail",
  },
  "auth/wrong-password": {
    summary: "error.auth.invalid_credentials.summary",
    detail: "error.auth.invalid_credentials.detail",
  },
  "auth/invalid-credential": {
    summary: "error.auth.invalid_credentials.summary",
    detail: "error.auth.invalid_credentials.detail",
  },
  "auth/too-many-requests": {
    summary: "error.auth.too_many_requests.summary",
    detail: "error.auth.too_many_requests.detail",
  },
  "auth/network-request-failed": {
    summary: "error.auth.network_error.summary",
    detail: "error.auth.network_error.detail",
  },
  "auth/user-disabled": {
    summary: "error.auth.user_disabled.summary",
    detail: "error.auth.user_disabled.detail",
  },
  "auth/invalid-email": {
    summary: "error.auth.invalid_email.summary",
    detail: "error.auth.invalid_email.detail",
  },
  "auth/requires-recent-login": {
    summary: "error.auth.requires_relogin.summary",
    detail: "error.auth.requires_relogin.detail",
  },
  "auth/email-already-in-use": {
    summary: "error.auth.email_in_use.summary",
    detail: "error.auth.email_in_use.detail",
  },
  "auth/weak-password": {
    summary: "error.auth.weak_password.summary",
    detail: "error.auth.weak_password.detail",
  },
};

const FUNCTIONS_ERROR_MAP: Record<string, ErrorResult> = {
  "functions/permission-denied": {
    summary: "error.codes.permission_denied.summary",
    detail: "error.codes.permission_denied.detail",
  },
  "functions/invalid-argument": {
    summary: "error.codes.invalid_argument.summary",
    detail: "error.codes.invalid_argument.detail",
  },
  "functions/internal": {
    summary: "error.codes.internal.summary",
    detail: "error.codes.internal.detail",
  },
  "functions/unavailable": {
    summary: "error.codes.unavailable.summary",
    detail: "error.codes.unavailable.detail",
  },
  "functions/unauthenticated": {
    summary: "error.codes.unauthenticated.summary",
    detail: "error.codes.unauthenticated.detail",
  },
  "functions/already-exists": {
    summary: "error.codes.already_exists.summary",
    detail: "error.codes.already_exists.detail",
  },
  "functions/not-found": {
    summary: "error.codes.not_found.summary",
    detail: "error.codes.not_found.detail",
  },
  "functions/aborted": {
    summary: "error.codes.aborted.summary",
    detail: "error.codes.aborted.detail",
  },
  "functions/failed-precondition": {
    summary: "error.codes.failed_precondition.summary",
    detail: "error.codes.failed_precondition.detail",
  },
  "functions/resource-exhausted": {
    summary: "error.codes.resource_exhausted.summary",
    detail: "error.codes.resource_exhausted.detail",
  },
};

const FIRESTORE_ERROR_MAP: Record<string, ErrorResult> = {
  "permission-denied": {
    summary: "error.codes.permission_denied.summary",
    detail: "error.codes.permission_denied.detail",
  },
  "not-found": {
    summary: "error.codes.not_found.summary",
    detail: "error.codes.not_found.detail",
  },
  unavailable: {
    summary: "error.codes.unavailable.summary",
    detail: "error.codes.unavailable.detail",
  },
  unauthenticated: {
    summary: "error.codes.unauthenticated.summary",
    detail: "error.codes.unauthenticated.detail",
  },
  "failed-precondition": {
    summary: "error.codes.failed_precondition.summary",
    detail: "error.codes.failed_precondition.detail",
  },
  aborted: {
    summary: "error.codes.aborted.summary",
    detail: "error.codes.aborted.detail",
  },
  "already-exists": {
    summary: "error.codes.already_exists.summary",
    detail: "error.codes.already_exists.detail",
  },
};

const STORAGE_ERROR_MAP: Record<string, ErrorResult> = {
  "storage/unknown": {
    summary: "error.storage.unknown.summary",
    detail: "error.storage.unknown.detail",
  },
  "storage/unauthorized": {
    summary: "error.storage.unauthorized.summary",
    detail: "error.storage.unauthorized.detail",
  },
  "storage/object-not-found": {
    summary: "error.storage.object_not_found.summary",
    detail: "error.storage.object_not_found.detail",
  },
  "storage/quota-exceeded": {
    summary: "error.storage.quota_exceeded.summary",
    detail: "error.storage.quota_exceeded.detail",
  },
  "storage/unauthenticated": {
    summary: "error.codes.unauthenticated.summary",
    detail: "error.codes.unauthenticated.detail",
  },
  "storage/canceled": {
    summary: "error.storage.canceled.summary",
    detail: "error.storage.canceled.detail",
  },
  "storage/retry-limit-exceeded": {
    summary: "error.storage.retry_limit_exceeded.summary",
    detail: "error.storage.retry_limit_exceeded.detail",
  },
};

const SERVICE_MAPS: [string, Record<string, ErrorResult>][] = [
  ["auth/", AUTH_ERROR_MAP],
  ["functions/", FUNCTIONS_ERROR_MAP],
  ["storage/", STORAGE_ERROR_MAP],
];

const FILE_TOO_LARGE_PATTERN = /payload too large|file too large|too large|413/i;

const RESOURCE_WITH_SUMMARY_SUFFIX = "_with_resource";

// error.resources.mimbox links to the brand name, so white-labelled customers see their own
const BRAND_RESOURCES = new Set(["mimbox"]);

const resolveResourceLabel = (resource?: string): string => {
  if (resource) {
    const key = `error.resources.${resource}`;
    return i18n.global.te(key) ? i18n.global.t(key) : resource;
  }
  return i18n.global.t("error.resources.generic");
};

const applyResourceToSummary = (summaryKey: string): string => {
  const resourceKey = `${summaryKey}${RESOURCE_WITH_SUMMARY_SUFFIX}`;
  return i18n.global.te(resourceKey) ? resourceKey : summaryKey;
};

const isFileTooLargeError = (error: unknown): boolean => {
  const storageError = error as {
    customData?: { serverResponse?: string | null };
    message?: string;
  };
  const payload = [storageError.customData?.serverResponse, storageError.message]
    .filter(Boolean)
    .join("\n");
  return FILE_TOO_LARGE_PATTERN.test(payload);
};

const errorCode = (error: unknown): string | undefined =>
  typeof error === "object" && error !== null && "code" in error
    ? String((error as { code: unknown }).code)
    : undefined;

export function formatError(error: unknown, options?: FormatErrorOptions): ErrorResult {
  const resource = resolveResourceLabel(options?.resource);
  // Mid-sentence the label is lowercased ("This user already exists"), but not a brand name
  const params = {
    resource,
    resourceInSentence:
      options?.resource && BRAND_RESOURCES.has(options.resource)
        ? resource
        : resource.toLowerCase(),
  };

  const code = errorCode(error);
  if (code !== undefined) {
    if (code.startsWith("storage/") && isFileTooLargeError(error)) {
      return {
        summary: "error.storage.file_too_large.summary",
        detail: "error.storage.file_too_large.detail",
        params,
      };
    }

    for (const [prefix, map] of SERVICE_MAPS) {
      if (code.startsWith(prefix) && code in map) {
        const entry = map[code];
        return {
          summary: options?.resource ? applyResourceToSummary(entry.summary) : entry.summary,
          detail: entry.detail,
          params,
        };
      }
    }

    if (code in FIRESTORE_ERROR_MAP) {
      const entry = FIRESTORE_ERROR_MAP[code];
      return {
        summary: options?.resource ? applyResourceToSummary(entry.summary) : entry.summary,
        detail: entry.detail,
        params,
      };
    }
  }

  return { summary: "error.generic.summary", detail: "error.generic.detail" };
}
type ReportOnErrorOptions = {
  returnReport?: boolean;
  resource?: string;
};
export async function reportOnError<T>(
  promise: Promise<T>,
  opt: ReportOnErrorOptions & { returnReport: true },
): Promise<{ resolved: T; report?: ReturnType<typeof formatError> }>;
export async function reportOnError<T>(
  promise: Promise<T>,
  opt?: ReportOnErrorOptions & { returnReport?: false },
): Promise<T>;
export async function reportOnError<T>(
  promise: Promise<T>,
  opt: ReportOnErrorOptions = {},
): Promise<T | { resolved: T; report?: ReturnType<typeof formatError> }> {
  const toastStore = useToastStore();
  let thrownError: unknown;
  let report: ReturnType<typeof formatError> = {
    summary: "",
  };
  const resolved = await promise.catch((error) => {
    thrownError = error;
    const code = errorCode(error);
    debug.error("reportOnError", { resource: opt.resource, code, error });
    sentryClient?.captureException(error, {
      captureContext: { tags: { resource: opt.resource, code } },
    });
    try {
      report = formatError(error, { resource: opt.resource });
    } catch (formatterError) {
      // Never let a broken message hide the real error
      debug.error("formatError failed", formatterError);
      report = { summary: "error.generic.summary", detail: "error.generic.detail" };
    }
    return null as T;
  });
  if (opt.returnReport) {
    if (thrownError) {
      report.summary = i18n.global.t(report.summary, report.params ?? {});
      report.detail = report.detail ? i18n.global.t(report.detail, report.params ?? {}) : undefined;
      return {
        resolved,
        report,
      };
    }
    return {
      resolved,
    };
  }

  if (thrownError) {
    toastStore.error(report);
    throw thrownError;
  }
  return resolved;
}
